import api from '@/lib/services/general.service'

export const state = {
  groups: [],
  // Focus column (Completed / Waiting / Remaining). Backend doesn't supply this yet,
  // so it stays empty and the column simply doesn't render until `data.focus` arrives.
  focus: [],
  isLoading: false,
  loaded: false,
  // No drill-down state here any more (busy-my-web#336). `activeGroup` / `groupTimelines` /
  // `groupLoading` held the clicked roll-up line and the threads behind it for the listing home
  // drew in its middle; home no longer draws roll-up lines, so nothing could set them and nothing
  // could read them. An Ongoing count unfolds inside the strip instead (components/Home/
  // FollowUps.vue + lib/homeFollowUps.js `ongoingGroupRows`), which needs no store.
  // How many sender clusters the Email Workspace would show. Drives the
  // "Emails need attention" follow-up (busy-my-web#16 step 5) — the row exists
  // only when there is actually something piling up.
  emailGroupCount: 0,
  // The OPEN THREAD's strip (/emails/timeline/:id, frame 5017:86689): at most four follow-ups and
  // the thread's Focus lists. Asked for on its own, after the message is on screen — the message
  // never waits for it — and keyed by the thread so a stale answer for the previous thread is
  // dropped rather than drawn.
  thread: { id: null, items: [], focus: [], loading: false, loaded: false },
}

export const getters = {
  groups: s => s.groups,
  focus: s => s.focus,
  isLoading: s => s.isLoading,
  loaded: s => s.loaded,
  emailGroupCount: s => s.emailGroupCount,
  thread: s => s.thread,
}

export const actions = {
  // Loads the cached "what do I need to follow up?" plan: 4 summary lines, each carrying its timelines.
  async fetch({ commit, rootGetters }) {
    // A guest has no follow-ups to ask for: `timeline/follow-ups` is signed-in only, and the BFF
    // answers a signed-out call with a redirect to the identity provider — one bounce per poll
    // (2026-08-29). Same guard as `emailSession/loadBriefing`; the lists simply stay empty.
    if (!rootGetters['user/isAuthenticated']) return
    commit('SET_LOADING', true)
    try {
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
      const response = await api.get('timeline/follow-ups', { timeZoneId })
      const data = response?.data ?? {}
      commit('SET_GROUPS', Array.isArray(data.groups) ? data.groups : [])
      commit('SET_FOCUS', Array.isArray(data.focus) ? data.focus : [])
    } catch {
      commit('SET_GROUPS', [])
      commit('SET_FOCUS', [])
    } finally {
      commit('SET_LOADING', false)
      commit('SET_LOADED', true)
    }
  },

  // The Email Workspace's own count, for the "Emails need attention" row
  // (busy-my-web#16 step 5). Its own action rather than part of `fetch`: the
  // follow-ups plan is the page's headline and must not wait on a second call,
  // and a mailbox that answers slowly should cost the row, not the page.
  //
  // Same defaults the Workspace itself uses, so the row's promise and the page
  // it opens agree about what a "group" is.
  async fetchEmailGroupCount({ commit }) {
    try {
      const response = await api.insert('email/groups', { minCount: 2, take: 50 })
      const groups = response?.data?.data ?? response?.data ?? []
      commit('SET_EMAIL_GROUP_COUNT', Array.isArray(groups) ? groups.length : 0)
    } catch {
      // No row rather than a wrong one.
      commit('SET_EMAIL_GROUP_COUNT', 0)
    }
  },

  // `openGroup` / `clearGroup` are gone with the drill-down listing (busy-my-web#336) — with them
  // the `POST timeline/by-ids` read they existed for, which nothing else in this module made.
  //
  // The open thread's follow-ups (busy-main-bff `GET timeline/{id}/follow-ups`): four lines at
  // most and the Focus lists. Fire-and-forget from the thread page; never awaited by the message.
  // `refresh` skips the BFF's cached answer — after something was created on the thread, the Focus
  // list must carry it now, not in six hours.
  async fetchThread({ commit, rootGetters, state: s }, payload) {
    const timelineId = typeof payload === 'object' && payload ? payload.timelineId : payload
    const refresh = typeof payload === 'object' && payload ? !!payload.refresh : false
    if (!timelineId || !rootGetters['user/isAuthenticated']) return
    // A refresh keeps what is on screen while the new answer comes (no flash to three dots).
    if (!refresh || s.thread.id !== timelineId) commit('SET_THREAD', { id: timelineId, items: [], focus: [], loading: true, loaded: false })
    try {
      const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
      const response = await api.get(`timeline/${timelineId}/follow-ups`, refresh ? { timeZoneId, refresh: true } : { timeZoneId })
      if (s.thread.id !== timelineId) return // moved on to another thread meanwhile
      const data = response?.data?.data ?? response?.data ?? {}
      const items = (Array.isArray(data.groups) ? data.groups : [])
        .flatMap(g => (Array.isArray(g.items) ? g.items : []))
        .filter(i => i && i.label)
        .slice(0, 4)
      const focus = Array.isArray(data.focus?.sections) ? data.focus.sections : []
      commit('SET_THREAD', { id: timelineId, items, focus, loading: false, loaded: true })
    } catch {
      if (s.thread.id === timelineId) commit('SET_THREAD', { id: timelineId, items: [], focus: [], loading: false, loaded: true })
    }
  },
  clearThread({ commit }) {
    commit('SET_THREAD', { id: null, items: [], focus: [], loading: false, loaded: false })
  },
  // A Focus row ticked on the thread page: the task flips done/undone in place, then on the server.
  async toggleThreadTask({ commit, dispatch, state: s }, item) {
    if (!item?.taskId) return
    const done = !item.done
    const focus = s.thread.focus.map(sec => ({ ...sec, items: sec.items.map(i => (i.taskId === item.taskId ? { ...i, done } : i)) }))
    commit('SET_THREAD', { ...s.thread, focus })
    try {
      await dispatch('task/updateStatus', { ids: [item.taskId], status: done ? 'done' : 'none' }, { root: true })
    } catch {
      /* the row keeps the optimistic state; the next load says the truth */
    }
  },
}

export const mutations = {
  SET_GROUPS(s, val) { s.groups = val },
  SET_EMAIL_GROUP_COUNT(s, val) { s.emailGroupCount = Number(val) || 0 },
  SET_FOCUS(s, val) { s.focus = val },
  SET_LOADING(s, val) { s.isLoading = val },
  SET_LOADED(s, val) { s.loaded = val },
  SET_THREAD(s, val) { s.thread = val },
}
